import { useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Modal, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationWithdraw } from '../../libs/ohat-api';
import { useAuth } from '../../hooks/useAuth';
import { usePlan } from '../../hooks/usePlan';
import { getApiErrorMessage } from '../../utils/apiError';

export type Props = {
  /** 退会対象の組織ID */
  organizationId: string;
};

/**
 * 退会（サービス退会）パネル。owner にのみ表示する。
 * 削除予定日や復旧手段はユーザーに提示しない（取り消せない操作として案内する）。
 * 成功後は退会完了画面へ遷移し、そこでサインアウトする。
 */
export const WithdrawalPanel = ({ organizationId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { message } = App.useApp();
  const { mutateAsync: withdraw, isPending: isWithdrawing } =
    useOrganizationWithdraw();
  const { isLight, planInfo } = usePlan();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // 継続課金（自動更新）が有効な間は退会できない。あくまでUX用の先出し判定で、
  // 正はAPI側の 409（プラン情報の取得ラグや取りこぼしはサーバー側で弾かれる）。
  const hasRecurringBilling = isLight && !planInfo?.cancelAtPeriodEnd;

  // 退会処理中は誤操作で閉じられないようにする。
  const handleCancel = () => {
    if (isWithdrawing) return;
    setConfirmOpen(false);
  };

  const handleOk = async () => {
    try {
      await withdraw({ id: organizationId });
    } catch (e) {
      // 失敗時はモーダルを開いたままにして再試行できるようにする。
      message.error(getApiErrorMessage(e, t('withdrawal.failed')));
      return;
    }
    navigate('/withdraw/complete', { replace: true });
  };

  if (!auth.isOwner()) return null;

  return (
    <Card
      title={t('withdrawal.sectionTitle')}
      style={{ marginTop: 16, borderColor: '#ffccc7' }}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
        {t('withdrawal.sectionDesc')}
      </Typography.Paragraph>
      {hasRecurringBilling && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title={t('withdrawal.billingActiveTitle')}
          description={t('withdrawal.billingActiveDesc')}
          action={
            <Button size="small" onClick={() => navigate('/plan')}>
              {t('menu.plan')}
            </Button>
          }
        />
      )}
      <Button
        danger
        block
        shape="round"
        disabled={hasRecurringBilling}
        loading={isWithdrawing}
        onClick={() => setConfirmOpen(true)}
      >
        {t('withdrawal.action')}
      </Button>
      <Modal
        title={t('withdrawal.confirmTitle')}
        open={confirmOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t('withdrawal.confirmOk')}
        cancelText={t('common.cancel')}
        // チェックが入るまでは退会を実行させない。
        okButtonProps={{ danger: true, disabled: !acknowledged }}
        cancelButtonProps={{ disabled: isWithdrawing }}
        confirmLoading={isWithdrawing}
        closable={!isWithdrawing}
        maskClosable={false}
        keyboard={false}
        // 次に開いたときに未チェックへ戻す。
        afterClose={() => setAcknowledged(false)}
      >
        <Typography.Paragraph>
          {t('withdrawal.confirmDesc')}
        </Typography.Paragraph>
        <Checkbox
          checked={acknowledged}
          disabled={isWithdrawing}
          onChange={(e) => setAcknowledged(e.target.checked)}
        >
          {t('withdrawal.confirmAcknowledge')}
        </Checkbox>
      </Modal>
    </Card>
  );
};
